import type { Visualizer } from './types';
import { clearBg, drawHud } from './types';

/** Perspective tunnel with rib rings + speed tied to energy. */
export const tunnelViz: Visualizer = {
  id: 'tunnel',
  label: 'Tunnel',
  draw(frame) {
    const { ctx, w, h, bands, freq, t } = frame;
    clearBg(ctx, w, h, 0.5);
    const cx = w / 2;
    const cy = h / 2;
    const segs = 36;
    const spin = t * (0.35 + bands.mids * 0.8);
    const speed = t * (2.2 + bands.energy * 4.5 + bands.kick * 1.5);

    // vignette base
    const vg = ctx.createRadialGradient(cx, cy, Math.min(w, h) * 0.05, cx, cy, Math.min(w, h) * 0.7);
    vg.addColorStop(0, 'rgba(10, 24, 32, 0.15)');
    vg.addColorStop(1, 'rgba(2, 4, 8, 0.95)');
    ctx.fillStyle = vg;
    ctx.fillRect(0, 0, w, h);

    for (let i = segs; i >= 0; i--) {
      const z = (i + (speed % 1)) / segs;
      const depth = z * z;
      const scale = 0.06 + depth * 1.45;
      const rw = w * scale * 0.52;
      const rh = h * scale * 0.42;
      const f = freq[Math.floor(((segs - i) / segs) * (freq.length - 1))] / 255;
      const rot = spin * (0.3 + (1 - z) * 0.7);

      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(rot * 0.15);
      ctx.translate(-cx, -cy);

      const alpha = 0.12 + (1 - z) * 0.55 + f * 0.2;
      ctx.strokeStyle = `hsla(${168 + z * 50 + bands.highs * 40 + f * 20}, 92%, ${42 + f * 32}%, ${alpha})`;
      ctx.lineWidth = 1.2 + (1 - z) * 5 + bands.beat * 2.5;

      // rounded rect ring
      const x = cx - rw / 2;
      const y = cy - rh / 2;
      const r = Math.min(rw, rh) * 0.08;
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.arcTo(x + rw, y, x + rw, y + rh, r);
      ctx.arcTo(x + rw, y + rh, x, y + rh, r);
      ctx.arcTo(x, y + rh, x, y, r);
      ctx.arcTo(x, y, x + rw, y, r);
      ctx.closePath();
      ctx.stroke();

      if (i % 3 === 0) {
        ctx.strokeStyle = `rgba(86,240,200,${0.03 + bands.energy * 0.06})`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(cx - w * 0.01, cy - h * 0.01);
        ctx.moveTo(x + rw, y);
        ctx.lineTo(cx + w * 0.01, cy - h * 0.01);
        ctx.moveTo(x, y + rh);
        ctx.lineTo(cx - w * 0.01, cy + h * 0.01);
        ctx.moveTo(x + rw, y + rh);
        ctx.lineTo(cx + w * 0.01, cy + h * 0.01);
        ctx.stroke();
      }
      ctx.restore();
    }

    const core = ctx.createRadialGradient(cx, cy, 2, cx, cy, Math.min(w, h) * (0.12 + bands.kick * 0.08));
    core.addColorStop(0, `rgba(255,255,255,${0.3 + bands.kick * 0.45})`);
    core.addColorStop(0.35, `rgba(86,240,200,${0.18 + bands.energy * 0.25})`);
    core.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = core;
    ctx.fillRect(0, 0, w, h);
    drawHud(frame);
  },
};
